import React, { useState, useMemo, useEffect, useRef } from "react";
import { httpClient } from '../../../HttpClient';
import "../Suivi.css";
import { IoIosSearch } from "react-icons/io";
import { CiFilter } from "react-icons/ci";
import { X } from "lucide-react";
import FicheClientOverlay from "../../Portefeuille/FicheClientOverlay";
import { missionCommencee, missionActive, estBloque, classeLigneBloquee } from "./suiviProdUtils";
import BadgeBloque from "./BadgeBloque";

function SuiviTVARSI({ suivi, loading, refetch }) {
  const [selectedYear, setSelectedYear] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRows, setFilterRows] = useState([
    { column: "collaborateur", selectedValues: [] },
  ]);
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const overlayRef = useRef(null);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [selectedClient, setSelectedClient] = useState({});
  const [activeSection, setActiveSection] = useState(null);

  // Fiche client partagée (mêmes états que SuiviJuridiqueAGO / SuiviBilan)
  const [ficheClientOpen, setFicheClientOpen] = useState(false);
  const [ficheClientData, setFicheClientData] = useState(null);
  const [ficheClientLoading, setFicheClientLoading] = useState(false);
  const ficheClientCache = useRef({});

  const showClientDetails = async (client) => {
    setFicheClientOpen(true);
    if (ficheClientCache.current[client.id]) {
      setFicheClientData(ficheClientCache.current[client.id]);
      setFicheClientLoading(false);
      return;
    }
    setFicheClientData(client);
    setFicheClientLoading(true);
    try {
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/clients/${client.id}`
      );
      ficheClientCache.current[client.id] = data;
      setFicheClientData(data);
    } catch (err) {
      console.error("Erreur chargement client:", err);
    } finally {
      setFicheClientLoading(false);
    }
  };

  // Fermer filtre si clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) {
        setShowFilterOverlay(false);
      }
    };
    if (showFilterOverlay) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterOverlay]);

  // 🔹 Calcul des années disponibles
  const getAvailableYears = (clients) => {
    if (!clients || clients.length === 0) return [];

    let years = [];

    clients.forEach((client) => {
      const clotureYear = new Date(client.dateCloture).getFullYear();
      if (!isNaN(clotureYear)) years.push(clotureYear);

      client.SuiviTVARSI?.forEach((entry) => {
        if (entry.annees) years.push(entry.annees);
      });
    });

    years = [...new Set(years)].sort((a, b) => a - b);
    const maxYear = Math.max(...years);
    years.push(maxYear + 1);
    return years;
  };

  const availableYears = getAvailableYears(suivi);

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      if (availableYears.includes(2025)) {
        setSelectedYear(2025);
      } else {
        setSelectedYear(Math.max(...availableYears));
      }
    }
  }, [availableYears, selectedYear]);

  // 🔹 Valeurs uniques pour les filtres
  const uniqueValues = useMemo(() => {
    const values = {
      collaborateur: Array.from(new Set(suivi.map((c) => c.collaborateur))),
      expertComptable: Array.from(new Set(suivi.map((c) => c.expertComptable))),
      regimeTVA: Array.from(new Set(suivi.map((c) => c.regimeTVA))),
      baseTVA: Array.from(
        new Set(
          suivi
            .flatMap((c) => c.SuiviTVARSI?.map((e) => e.baseTVA))
            .filter(Boolean)
        )
      ),
    };
    return values;
  }, [suivi]);

  // 🔹 Gestion filtres dynamiques
  const handleAddFilter = () => {
    setFilterRows((prev) => [
      ...prev,
      { column: "collaborateur", selectedValues: [] },
    ]);
  };

  const handleRemoveFilter = (indexToRemove) => {
    setFilterRows((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleFilterChange = (index, field, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) =>
        i === index ? { ...f, [field]: value, selectedValues: [] } : f
      )
    );
  };

  const handleCheckboxValueChange = (index, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        const selectedValues = f.selectedValues || [];
        const updated = selectedValues.includes(value)
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value];
        return { ...f, selectedValues: updated };
      })
    );
  };

  const handleResetFilters = () => {
    setFilterRows([{ column: "collaborateur", selectedValues: [] }]);
    setSearchTerm("");
  };

  // 🔹 Filtrage global (même logique que SuiviSocial)
  const filteredData = useMemo(() => {
    return suivi.filter((client) => {
      const matchesSearch = client.nom
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesFilters = filterRows.every((filter) => {
        if (!filter.selectedValues || filter.selectedValues.length === 0)
          return true;

        if (filter.column === "baseTVA") {
          return client.SuiviTVARSI?.some((t) =>
            filter.selectedValues.includes(t.baseTVA)
          );
        }
        return filter.selectedValues.includes(client[filter.column]);
      });

      const clotureYear = new Date(client.dateCloture).getFullYear();
      const matchesYear =
        !selectedYear ||
        selectedYear >= clotureYear ||
        client.SuiviTVARSI?.some((t) => t.annees === selectedYear);

      // Mission pas encore débutée cette année-là → masqué ; client archivé
      // depuis une année antérieure → masqué aussi.
      const missionDebutee =
        !selectedYear ||
        (missionCommencee(client, selectedYear) &&
          missionActive(client, selectedYear));

      return matchesSearch && matchesFilters && matchesYear && missionDebutee;
    });
  }, [suivi, searchTerm, filterRows, selectedYear]);

  // --- 🔹 Gestion de l'édition
  const [currentRecord, setCurrentRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleEditClick = (entry, client, section) => {
    setCurrentRecord(entry);
    setSelectedClient(client);
    setActiveSection(section); // 🔹 on garde la section cliquée
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRecord((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    if (!currentRecord || !selectedYear) {
      console.error("Aucune donnée sélectionnée ou année invalide");
      return;
    }

    // 🧮 Convertir les valeurs numériques avant envoi
    const payload = {
      ...currentRecord,
      baseTVA:
        currentRecord.baseTVA !== "" && currentRecord.baseTVA != null
          ? parseFloat(currentRecord.baseTVA)
          : null,
      montantAcompte1:
        currentRecord.montantAcompte1 !== "" &&
        currentRecord.montantAcompte1 != null
          ? parseFloat(currentRecord.montantAcompte1)
          : null,
      montantAcompte2:
        currentRecord.montantAcompte2 !== "" &&
        currentRecord.montantAcompte2 != null
          ? parseFloat(currentRecord.montantAcompte2)
          : null,
    };

    setLoadingUpdate(true);
    setShowModal(false);

    try {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/tvaRSI/${selectedYear}/${selectedClient.id}`,
        payload
      );
    } catch (err) {
      console.error("Erreur update SuiviTVARSI", err);
    } finally {
      setLoadingUpdate(false);
      if (refetch) refetch();
    }
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
          <div className="h-9 w-64 rounded-md bg-slate-200 animate-pulse" />
          <div className="flex items-center gap-5">
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
          </div>
        </div>
        <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#6e1a36]">
                {["Dossier", "Base TVA", "Acompte 1", "Acompte 2"].map((col, i) => (
                  <th key={i} className="px-3 py-3.5 text-left">
                    <div className="h-3 rounded bg-[#ffffff33] animate-pulse" style={{ width: "70%" }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {["50%", "25%", "20%", "20%"].map((w, j) => (
                    <td key={j} className="px-3 py-3">
                      <div className="h-3 rounded bg-slate-200 animate-pulse" style={{ width: w }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const getRowColor = (statut) => {
    if (!statut || statut === "" || statut === "Saisi") return "transparent";
    if (statut === "Demander l'accord de paiement") return "#fff8dc";
    if (statut === "Statut paiement") return "#e9f6ec";
    return "transparent";
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
        <div className="relative flex-1 max-w-sm">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filtrer par nom du dossier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 w-full rounded-md bg-slate-50 border border-slate-200 text-sm focus:bg-white focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-5">
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-slate-300 rounded-md
                                    h-9 px-2 text-xs leading-tight w-20
                                    bg-white"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowFilterOverlay(!showFilterOverlay)}
            className={`flex items-center gap-2 h-9 px-3 rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
          >
            <CiFilter className="w-3 h-3" />
            Filtrer
          </button>
        </div>
        {showFilterOverlay && (
          <div
            ref={overlayRef}
            className="fixed top-[40%] left-[65%] -translate-x-1/2 bg-white border border-slate-200 rounded-lg p-4 shadow-lg z-50 w-2/5 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between mb-2">
              <p className="font-semibold">Filtres avancés</p>
              <button
                className="text-sm text-slate-600 hover:text-slate-800"
                onClick={handleResetFilters}
              >
                Réinitialiser
              </button>
            </div>
            {filterRows.map((filter, index) => (
              <div key={index} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <select
                    value={filter.column}
                    onChange={(e) =>
                      handleFilterChange(index, "column", e.target.value)
                    }
                    className="border border-slate-200 rounded-md p-1 text-sm"
                  >
                    {Object.keys(uniqueValues).map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveFilter(index)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    - Supprimer
                  </button>
                </div>
                <div className="flex flex-col ml-2">
                  {uniqueValues[filter.column]?.map((value) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={filter.selectedValues.includes(value)}
                        onChange={() => handleCheckboxValueChange(index, value)}
                        className="mr-2"
                      />
                      {value || "(vide)"}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button
              className="text-sm text-slate-700 hover:text-slate-900 mt-2"
              onClick={handleAddFilter}
            >
              + Ajouter filtre
            </button>
          </div>
        )}
      </div>

      {/* Tableau principal */}
      <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 250px)", minHeight: "420px" }}
        >
          <table className="w-full" style={{ fontSize: "0.72rem" }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#6e1a36] text-xs">
                <th className="px-2 py-3.5 text-white font-semibold text-left">
                  Dossier
                </th>
                <th className="px-2 py-3.5 text-white font-semibold text-center">
                  Base TVA
                </th>
                <th className="px-2 py-3.5 text-white font-semibold text-center">
                  Acompte 1
                </th>
                <th className="px-2 py-3.5 text-white font-semibold text-center">
                  Acompte 2
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-12 text-sm text-slate-500"
                  >
                    Aucun client trouvé
                  </td>
                </tr>
              )}
              {filteredData.map((client) => {
                const entries =
                  client.SuiviTVARSI?.filter(
                    (e) => !selectedYear || e.annees === selectedYear
                  ) || [];

                const displayEntries = entries.length > 0 ? entries : [{}];

                return (
                  <React.Fragment key={client.id}>
                    {displayEntries.map((entry, i) => (
                      <tr
                        key={`${client.id}-${i}`}
                        className={`hover:bg-slate-50/50 transition-colors ${classeLigneBloquee(client)}`}
                      >
                        <td
                          onClick={() => i === 0 && showClientDetails(client)}
                          className="px-2 py-2 font-medium text-slate-900 cursor-pointer hover:text-[#9B1B5D] transition-colors"
                        >
                          {i === 0 ? client.nom : ""}
                          {i === 0 && estBloque(client) && <BadgeBloque />}
                        </td>
                        <td
                          className="px-2 py-1.5 text-center text-slate-700 cursor-pointer"
                          onClick={() =>
                            handleEditClick(entry, client, "baseTVA")
                          }
                        >
                          {entry.baseTVA || ""}
                        </td>

                        <td
                          className="px-2 py-1.5 text-center text-slate-700 cursor-pointer"
                          style={{
                            backgroundColor: getRowColor(
                              entry.statutDeclaration1
                            ),
                          }}
                          onClick={() => handleEditClick(entry, client, "ac1")}
                        >
                          {entry.montantAcompte1 !== "" &&
                          entry.montantAcompte1 != null
                            ? entry.montantAcompte1
                            : entry.baseTVA
                            ? (entry.baseTVA * 0.55).toFixed(2)
                            : ""}
                        </td>

                        <td
                          className="px-2 py-1.5 text-center text-slate-700 cursor-pointer"
                          style={{
                            backgroundColor: getRowColor(
                              entry.statutDeclaration2
                            ),
                          }}
                          onClick={() => handleEditClick(entry, client, "ac2")}
                        >
                          {entry.montantAcompte2 !== "" &&
                          entry.montantAcompte2 != null
                            ? entry.montantAcompte2
                            : entry.baseTVA
                            ? (entry.baseTVA * 0.4).toFixed(2)
                            : ""}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal édition */}
      {showModal && (
        <div className="overlayAC">
          <div className="inoverlayLead">
            <button
              aria-label="Fermer"
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              style={{ zIndex: 10000 }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="w-[85%] mx-auto mt-5">
              <div className="border-b-4 border-[#8B1538] pb-4 mb-6">
                <h2 className="text-xl font-bold text-slate-900 m-0">
                  TVA RSI — {selectedClient.nom || "Client inconnu"}
                </h2>
                <p className="text-xs text-slate-500 mt-1 m-0">
                  Année {selectedYear}
                </p>
              </div>

              {/* --- Section Base TVA --- */}
              {activeSection === "baseTVA" && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Base TVA
                  </label>
                  <input
                    className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    name="baseTVA"
                    type="number"
                    value={currentRecord.baseTVA || ""}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              {/* --- Section AC1 --- */}
              {activeSection === "ac1" && (
                <div className="flex flex-col">
                  <h4 className="text-sm font-bold text-[#9B1B5D] m-0 mb-4">
                    Acompte 1 — {selectedYear}
                  </h4>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Montant Acompte 1
                    </label>
                    <input
                      name="montantAcompte1"
                      type="number"
                      value={currentRecord.montantAcompte1 || ""}
                      onChange={handleInputChange}
                      placeholder={
                        currentRecord.baseTVA
                          ? `${(currentRecord.baseTVA * 0.55).toFixed(2)}`
                          : "55% Base TVA"
                      }
                      className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Statut Déclaration 1
                    </label>
                    <select
                      name="statutDeclaration1"
                      value={currentRecord.statutDeclaration1 || ""}
                      onChange={handleInputChange}
                      className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    >
                      <option value="Saisi">Saisi</option>
                      <option value="Demander l'accord de paiement">
                        Demander l'accord de paiement
                      </option>
                      <option value="Statut paiement">Statut paiement</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Commentaire
                    </label>
                    <textarea
                      className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                      name="commentaire1"
                      value={currentRecord.commentaire1 || ""}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* --- Section AC2 --- */}
              {activeSection === "ac2" && (
                <div className="flex flex-col">
                  <h4 className="text-sm font-bold text-[#9B1B5D] m-0 mb-4">
                    Acompte 2 — {selectedYear}
                  </h4>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Montant Acompte 2
                    </label>
                    <input
                      name="montantAcompte2"
                      type="number"
                      value={currentRecord.montantAcompte2 || ""}
                      onChange={handleInputChange}
                      placeholder={
                        currentRecord.baseTVA
                          ? `${(currentRecord.baseTVA * 0.4).toFixed(2)}`
                          : "40% Base TVA"
                      }
                      className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Statut Déclaration 2
                    </label>
                    <select
                      name="statutDeclaration2"
                      value={currentRecord.statutDeclaration2 || ""}
                      onChange={handleInputChange}
                      className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    >
                      <option value="Saisi">Saisi</option>
                      <option value="Demander l'accord de paiement">
                        Demander l'accord de paiement
                      </option>
                      <option value="Statut paiement">Statut paiement</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Commentaire
                    </label>
                    <textarea
                      className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                      name="commentaire2"
                      value={currentRecord.commentaire2 || ""}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* --- Boutons communs --- */}
              <div className="flex justify-end gap-3 mt-6 mb-10">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-5 h-10 rounded-md font-medium text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={loadingUpdate}
                  className="px-5 h-10 rounded-md font-semibold text-sm bg-[#840040] hover:bg-[#6e1a36] text-white disabled:opacity-50"
                >
                  {loadingUpdate ? "En cours..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FicheClientOverlay
        client={ficheClientData}
        open={ficheClientOpen}
        loading={ficheClientLoading}
        initialTab="general"
        onClose={() => setFicheClientOpen(false)}
        onClientUpdated={(updated) => {
          ficheClientCache.current[updated.id] = updated;
          setFicheClientData(updated);
        }}
      />
    </div>
  );
}

export default SuiviTVARSI;
